import React, { Component, Fragment } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from "reactstrap";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import { FormattedNumber } from "../../FormattedNumber/FormattedNumber";
import classnames from "classnames";
import OrderInlineDetail from "../../Order/InlineDetail";
import ItemRow from "./ItemRow";
import style from "./OrderQuantityAssignment.module.scss";
import { Combobox } from "../Order/Field/Field";
import { OrderDayStatus } from "../../../utils/Order/Order";

class OrderQuantityAssignment extends Component {
  constructor(props) {
    super(props);

    this.state = {
      info: {
        currentOrderVersion: null,
        minimumQuantity: null,
        incrementQuantity: null,
      },
      distributionType: "FIFO", // FIFO, LIFO, Prorate
      selectedDate: null,
      days: null,
      accounts: null,
      confirm: {
        enabled: false,
        loading: false,
      },
      error: null,
    };
  }

  componentDidMount() {
    const { order } = this.props;

    if (order.OrderId != 0) {
      // retrieve accounts assignment in the order
      //this.state.loadings.distribute = true;

      this.getAssignment(order.OrderId);
    }
  }

  getAssignment = (orderId) => {
    const { fetchOrderAccountsAssignment } = this.props;

    fetchOrderAccountsAssignment({ orderId })
      .then((res) => res.response.AccountsAssignment)
      .then((assignment) => {
        const info = {
          currentOrderVersion: parseInt(assignment.CurrentVersion, 10),
          minimumQuantity: parseFloat(assignment.MinimumQuantity),
          incrementQuantity: parseFloat(assignment.IncrementQuantity),
        };

        let selectedDate = null;
        const days = [];
        assignment.Day.forEach((d) => {
          const day = {
            date: d.Date,
            executedQuantity: parseFloat(d.ExecutedQuantity),
            bookedQuantity: parseFloat(d.BookedQuantity),
            quantityToAssign: parseFloat(d.QuantityToAssign),
            leftToAssign: parseFloat(d.QuantityToAssign),
            fixedClientQty: parseFloat(d.FixedClientQuantity),
            fixedPropQty: parseFloat(d.FixedPropQuantity),
          };
          days.push(day);

          if (selectedDate == null && day.quantityToAssign > 0) {
            selectedDate = day.date;
          }
        });

        const accounts = [];

        assignment.Account.forEach((a) => {
          const account = {
            accountId: parseInt(a.AccountId, 10),
            accountNumber: a.AccountNumber,
            accountName: a.AccountName,
            accountType: a.AccountType,
            accountInBalanceType: a.AccountInBalanceType,
            position: parseInt(a.Position, 10),
            askedQty: parseFloat(a.AskedQuantity),
            askedFullLiquidation: a.AskedFullLiquidation,
            totalBookedQty: parseFloat(a.TotalBookedQuantity),
            totalAssignedQty: parseFloat(a.TotalAssignedQuantity),
            days: [],
          };

          a.Day.forEach((accountDay) => {
            const day = {
              date: accountDay.Date,
              assignedQty: parseFloat(accountDay.AssignedQuantity),
              status: accountDay.Status,
            };
            account.days.push(day);

            if (day.status !== OrderDayStatus.BOOKED) {
              // If account is not booked for this day, we should subtract
              // it assigned quantity for this day' left to assign quantity.
              days.find((d) => d.date === day.date).leftToAssign -= day.assignedQty;
            }
          });

          accounts.push(account);
        });

        accounts.sort((a, b) => a.position - b.position);

        this.setState({
          info,
          selectedDate,
          days,
          accounts,
        });
      });
  };

  onToggle = () => {
    const { onToggle } = this.props;
    onToggle();
  };

  changeItem = (itemId, name, value) => {
    const { accounts, days, selectedDate } = this.state;

    const account = accounts.find((a) => a.accountId === itemId);
    const accountDay = account.days.find((d) => d.date === selectedDate);
    let differenceOfAssignedQty = accountDay.assignedQty;

    accountDay[name] = isNaN(value) || value == "" ? value : +value;
    accountDay[`error_${name}`] = undefined;

    // Track the change in assigned quantity.
    differenceOfAssignedQty -= accountDay.assignedQty;

    // Update total assigned quantity of the account.
    account.totalAssignedQty -= differenceOfAssignedQty;

    const day = days.find((d) => d.date === selectedDate);
    const modifiedDay = this.calcTotals(accounts);

    day.leftToAssign = modifiedDay.leftToAssign;

    this.setState({
      accounts,
      days,
      error: undefined,
    });
  };

  validate = () => {
    const { days, accounts, info } = this.state;
    const { minimumQuantity, incrementQuantity } = info;
    let hasErrors = false;
    let error;

    let totalAssignedAccount = 0;

    let modAccounts = accounts.map((account) => {
      let modAccount = account;

      modAccount.days = account.days.map((accountDay) => {
        let modAccountDay = accountDay;

        if (modAccountDay.assignedQty !== 0) {
          // Verify conditions only if quantity is bigger than zero
          if (
            modAccountDay.assignedQty === undefined ||
            modAccountDay.assignedQty === "" ||
            modAccountDay.assignedQty < 0
          ) {
            modAccountDay.error_assignedQty = "Required field";
            hasErrors = true;
          } else if (modAccount.totalAssignedQty > modAccount.askedQty) {
            modAccountDay.error_assignedQty = "Total assigned quantity is over asked quantity";
            hasErrors = true;
          } else if (modAccount.totalAssignedQty < minimumQuantity) {
            modAccountDay.error_assignedQty = "Total assigned quantity is less than minimum quantity";
            hasErrors = true;
          } else if (
            incrementQuantity > 1 &&
            incrementQuantity !== 0 &&
            (modAccount.totalAssignedQty - minimumQuantity) % incrementQuantity !== 0
          ) {
            modAccountDay.error_assignedQty =
              "Total assigned quantity is not a multiple of the increment quantity";
            hasErrors = true;
          }
        }

        totalAssignedAccount += modAccountDay.assignedQty;

        return modAccountDay;
      });

      return modAccount;
    });

    days.forEach((day) => {
      let totalDayAssignedQty = 0;
      let totalDayClientAssignedQty = 0;
      let totalDayPropAssignedQty = 0;
      modAccounts.forEach((account) => {
        const accountDay = account.days.find((accDay) => accDay.date === day.date);
        totalDayAssignedQty += accountDay.assignedQty;

        if (account.accountType.toLowerCase() === "inbalance") {
          totalDayPropAssignedQty += accountDay.assignedQty;
        } else {
          totalDayClientAssignedQty += accountDay.assignedQty;
        }
      });

      if (
        !(Math.abs(totalDayAssignedQty - day.executedQuantity) < minimumQuantity) &&
        totalDayAssignedQty !== day.executedQuantity
      ) {
        error = "Total assigned quantity differs from executed quantity in a day";
        hasErrors = true;
      } else if (totalDayPropAssignedQty < day.fixedPropQty) {
        error = "Fixed quantity for props was not respected.";
        hasErrors = true;
      } else if (totalDayClientAssignedQty < day.fixedClientQty) {
        error = "Fixed quantity for clients was not respected.";
        hasErrors = true;
      }
    });

    this.setState({
      accounts: modAccounts,
      error,
    });

    return !hasErrors;
  };

  onAssignmentUpdate = () => {
    if (this.validate()) {
      const { order } = this.props;
      const { info, accounts } = this.state;
      const { fetchUpdateOrderAccountsAssignment, onToggle } = this.props;

      const accountsToSend = accounts.map((account) => ({
        AccountId: account.accountId,
        Day: account.days.map((day) => ({
          Date: day.date,
          AssignedQuantity: day.assignedQty,
        })),
      }));

      this.setState({ confirm: { loading: true } });

      fetchUpdateOrderAccountsAssignment({
        orderId: order.OrderId,
        AccountsAssignment: {
          CurrentVersion: info.currentOrderVersion,
          Account: accountsToSend,
        },
      }).then((res) => {
        onToggle();
      });
    }
  };

  onDistributionApply = () => {
    const { info, distributionType, selectedDate, days, accounts } = this.state;
    const { minimumQuantity, incrementQuantity } = info;

    const day = days.find((d) => d.date === selectedDate);
    const { quantityToAssign } = day;

    let modifiedAccounts = null;

    let pendingQuantityToAssign = quantityToAssign;

    let totalRemainingQuantity = 0;

    accounts.forEach((account) => {
      const { totalAssignedQty, askedQty } = account;

      const accountDay = account.days.find((d) => d.date === selectedDate);

      if ([OrderDayStatus.LOCKED, OrderDayStatus.BOOKED].indexOf(accountDay.status) < 0) {
        // The account is still not locked or booked for this day.
        // We can modify the assigned quantity.

        const assignedQtyInOtherDays = totalAssignedQty - accountDay.assignedQty;

        totalRemainingQuantity += askedQty - assignedQtyInOtherDays;
      } else if (accountDay.status === OrderDayStatus.LOCKED) {
        // Remove the Locked quantity from the pending quantity to assign.
        // We can't modify this quantities.
        pendingQuantityToAssign -= accountDay.assignedQty;
      }
    });

    const prorateOverPendingQty =
      totalRemainingQuantity > 0 ? pendingQuantityToAssign / totalRemainingQuantity : 1.0;

    switch (distributionType) {
      case "FIFO":
      case "LIFO":
        if (distributionType == "LIFO") {
          accounts.sort((a, b) => b.position - a.position);
        }

        modifiedAccounts = accounts.map((account) => {
          const accountDay = account.days.find((d) => d.date === selectedDate);

          if ([OrderDayStatus.LOCKED, OrderDayStatus.BOOKED].indexOf(accountDay.status) >= 0) {
            // The account is already locked or booked for this day.
            // We can't modify the assigned quantity.
            return account;
          }

          let differenceOfAssignedQty = accountDay.assignedQty;

          const { askedQty, totalAssignedQty } = account;

          const assignedQtyInOtherDays = totalAssignedQty - accountDay.assignedQty;

          let assignedQty = 0;
          // Calculate pending account quantity for this day.
          const pendingAcctQty = askedQty - assignedQtyInOtherDays;

          if (pendingQuantityToAssign > 0 && pendingAcctQty > 0) {
            if (pendingQuantityToAssign >= minimumQuantity) {
              assignedQty = Math.min(pendingAcctQty, pendingQuantityToAssign);
            }
          }

          accountDay.assignedQty = assignedQty;

          pendingQuantityToAssign -= assignedQty;

          // Track the change in assigned quantity.
          differenceOfAssignedQty -= assignedQty;

          return {
            ...account,
            totalAssignedQty: account.totalAssignedQty - differenceOfAssignedQty,
          };
        });

        if (distributionType == "LIFO") {
          modifiedAccounts.sort((a, b) => a.position - b.position);
        }

        break;

      case "Prorate":
        modifiedAccounts = accounts.map((account) => {
          const accountDay = account.days.find((d) => d.date === selectedDate);

          if ([OrderDayStatus.LOCKED, OrderDayStatus.BOOKED].indexOf(accountDay.status) >= 0) {
            // The account is already locked or booked for this day.
            // We can't modify the assigned quantity.
            return account;
          }

          let differenceOfAssignedQty = accountDay.assignedQty;

          const { askedQty, totalAssignedQty } = account;

          const assignedQtyInOtherDays = totalAssignedQty - accountDay.assignedQty;
          // Calculate pending account quantity for this day.
          const pendingAcctQty = askedQty - assignedQtyInOtherDays;

          let assignedQty = pendingAcctQty * prorateOverPendingQty;

          const remainderOverIncrementQuantity = incrementQuantity > 0 ? assignedQty % incrementQuantity : 0;

          // We round down to the nearest increment.
          assignedQty -= remainderOverIncrementQuantity;

          if (remainderOverIncrementQuantity > incrementQuantity / 2) {
            // We have to round up, if the account is not full.
            if (pendingAcctQty + assignedQty + incrementQuantity <= askedQty) {
              // round up.
              assignedQty += incrementQuantity;
            }
          }

          accountDay.assignedQty = assignedQty;

          // Track the change in assigned quantity.
          differenceOfAssignedQty -= assignedQty;

          return {
            ...account,
            totalAssignedQty: account.totalAssignedQty - differenceOfAssignedQty,
          };
        });

        break;
      default:
    }

    const modifiedDay = this.calcTotals(modifiedAccounts);
    day.leftToAssign = modifiedDay.leftToAssign;

    this.setState({
      accounts: modifiedAccounts,
      days,
    });
  };

  calcTotals = (accounts) => {
    const { days, selectedDate } = this.state;

    const day = days.find((d) => d.date === selectedDate);

    let totalAssignedQuantityWithoutBook = 0;

    accounts.forEach((account) => {
      const accountDay = account.days.find((d) => d.date === selectedDate);
      if (!isNaN(accountDay.assignedQty)) {
        if (accountDay.status !== OrderDayStatus.BOOKED) {
          totalAssignedQuantityWithoutBook += accountDay.assignedQty;
        }
      }
    });

    day.leftToAssign = day.quantityToAssign - totalAssignedQuantityWithoutBook;

    return day;
  };

  render() {
    const { open, order } = this.props;
    const { info, distributionType, selectedDate, days, accounts, confirm, error } = this.state;

    const listOfDays = days ? days.map((d) => ({ id: d.date, description: d.date })) : [];
    const selectedDateDay = days ? days.find((d) => d.date === selectedDate) : {};

    return (
      <Modal isOpen={open} toggle={this.onToggle} className={style.modal}>
        <ModalHeader toggle={this.onToggle}>
          Quantity assignment
          <OrderInlineDetail order={order} />
        </ModalHeader>
        <ModalBody style={{ padding: "10px 15px", minHeight: "225px" }}>
          <div className={style.distributionType}>
            <label>Date</label>
            <Combobox
              value={selectedDate}
              items={listOfDays}
              onChange={(v) => this.setState({ selectedDate: v })}
              emptyLabel="Date"
              invalid={!!error}
              style={{ minWidth: "180px", marginRight: "10px" }}
            />
          </div>
          <DistributionType
            type={distributionType}
            onChange={(v) => this.setState({ distributionType: v })}
            onApply={this.onDistributionApply}
            minimum={info.minimumQuantity}
            increment={info.incrementQuantity}
          />

          {accounts &&
            accounts.map((a) => (
              <ItemRow
                account={a}
                selectedDate={selectedDate}
                id={a.accountId}
                key={`irow-${a.accountId}`}
                actions={{
                  change: this.changeItem,
                }}
              />
            ))}

          {error && (
            <Fragment>
              <hr />
              <Alert color="danger" className={style.alert}>
                {error}
              </Alert>
            </Fragment>
          )}
        </ModalBody>

        <ModalFooter style={{ padding: "12px 20px" }}>
          <FooterTotals totals={selectedDateDay} />

          <Button color="light" onClick={this.onToggle}>
            Close
          </Button>
          <LaddaButton
            className={classnames("btn btn-ladda", style.update)}
            loading={confirm.loading}
            onClick={this.onAssignmentUpdate}
            data-style={EXPAND_RIGHT}
          >
            Update
          </LaddaButton>
        </ModalFooter>
      </Modal>
    );
  }
}

const DistributionType = ({ type, error, enabled, onChange, onApply, minimum, increment }) => (
  <div className={style.distributionType}>
    <label>Distribute by</label>
    <Combobox
      value={type}
      enabled={enabled}
      items={[
        { id: "FIFO", description: "FIFO" },
        { id: "LIFO", description: "LIFO" },
        { id: "Prorate", description: "Prorate" },
      ]}
      onChange={(v) => onChange(v)}
      emptyLabel="Dist type"
      invalid={!!error}
      style={{ minWidth: "180px", marginRight: "10px" }}
    />
    <LaddaButton
      className={classnames("btn btn-ladda", style.applyDist)}
      onClick={onApply}
      data-style={EXPAND_RIGHT}
    >
      Apply
    </LaddaButton>
    <label>{`Minimum: ${minimum}  |  Increment: ${increment}`}</label>
  </div>
);

const Number = ({ value, dataTypeDecimalName }) => {
  if (value === null) {
    return "-";
  }

  return <FormattedNumber value={value} dataTypeDecimalName={dataTypeDecimalName} />;
};

const FooterTotals = ({ totals }) => (
  <div className={style.footerTotals}>
    <div style={{ marginRight: "40px", minWidth: "240px" }}>
      <table style={{ margin: "10px 0px" }}>
        <tbody>
          <tr>
            <td>Executed quantity</td>
            <td>
              <Number value={totals.executedQuantity || 0} dataTypeDecimalName="quantity" />
            </td>
          </tr>
          <tr>
            <td>Booked quantity</td>
            <td>
              <Number value={totals.bookedQuantity || 0} dataTypeDecimalName="quantity" />
            </td>
          </tr>
          {(totals.fixedClientQty > 0 || totals.fixedPropQty > 0) && (
            <tr>
              <td>Fixed</td>
              {totals.fixedClientQty > 0 && (
                <td>
                  - Clients (
                  <Number value={totals.fixedClientQty || 0} dataTypeDecimalName="quantity" />)
                </td>
              )}
              {totals.fixedPropQty > 0 && (
                <td>
                  - Prop (
                  <Number value={totals.fixedPropQty || 0} dataTypeDecimalName="quantity" />)
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>

    <div>
      <strong>Leftovers</strong>
      <table>
        <tbody>
          <tr>
            <td style={{ color: "#4dbd74", fontWeight: 500 }}>To assign</td>
            <td>
              <Number value={totals.leftToAssign || 0} dataTypeDecimalName="currency" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export default OrderQuantityAssignment;
